import { Link } from 'react-router-dom';
import { HiOutlineExternalLink } from 'react-icons/hi';
import AgreementPartiesSummary from './AgreementPartiesSummary';
import { CollectionAgreement, CollectionOrderStep } from '../collectionAgreementModel';
import { policyHasPhase } from '../agreementAccountRules';
import {
    ACCOUNT_KIND_LABEL,
    CollectionPolicy,
    OBLIGOR_LABEL,
    overdueLabel,
    preventiveLabel,
} from '../collectionModel';
import { collectionPolicyEditPath } from '../collectionRoutes';
import { formatDateTimeBR } from '../../../../../utils/DateUtils';

interface CollectionAgreementViewProps {
    agreement: CollectionAgreement;
    policy?: CollectionPolicy;
}

const ROLE_LABEL: Record<CollectionOrderStep['role'], string> = { ...OBLIGOR_LABEL, PAYER: 'Sacado', GUARANTOR: 'Avalistas' };

const ReadValue = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
        <p className="mt-1 text-sm text-gray-800 dark:text-gray-100">{children}</p>
    </div>
);

const YesNoBadge = ({ value }: { value: boolean }) => (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${value
        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
        : 'bg-gray-200 text-gray-700 dark:bg-slate-700 dark:text-slate-300'}`}>
        {value ? 'Sim' : 'Não'}
    </span>
);

const ReadOnlyOrderTable = ({ agreement, policy }: { agreement: CollectionAgreement; policy: CollectionPolicy }) => {
    const hasPreventive = policyHasPhase(policy, 'preventive');
    const hasOverdue = policyHasPhase(policy, 'overdue');
    return (
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700">
            <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left dark:bg-slate-800/60">
                    <tr>
                        <th className="px-4 py-2 font-semibold text-gray-700 dark:text-gray-200">Ordem de cobrança</th>
                        <th className="px-4 py-2">
                            <span className="block font-semibold text-gray-700 dark:text-gray-200">Antes do vencimento</span>
                            <span className="text-xs font-normal text-gray-500">{hasPreventive ? preventiveLabel(policy.schedules.preventive) : 'A política não cobra nesta fase'}</span>
                        </th>
                        <th className="px-4 py-2">
                            <span className="block font-semibold text-gray-700 dark:text-gray-200">Depois do vencimento</span>
                            <span className="text-xs font-normal text-gray-500">{hasOverdue ? overdueLabel(policy.schedules.overdue) : 'A política não cobra nesta fase'}</span>
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                    {agreement.collectionOrder.map((step, index) => (
                        <tr key={step.role}>
                            <td className="px-4 py-3 text-gray-800 dark:text-gray-100">{index + 1}. {ROLE_LABEL[step.role]}</td>
                            <td className="px-4 py-3"><YesNoBadge value={step.preventive && hasPreventive} /></td>
                            <td className="px-4 py-3"><YesNoBadge value={step.overdue && hasOverdue} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const CollectionAgreementView = ({ agreement, policy }: CollectionAgreementViewProps) => (
    <section className="rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-5 py-4 dark:border-slate-700">
            <div className="flex items-center gap-3">
                <YesNoBadge value={agreement.collect} />
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Cobrança</h3>
            </div>
            <div className="flex flex-wrap items-center gap-6 text-sm">
                <span className="text-gray-600 dark:text-gray-300">
                    Conta <strong className="text-gray-800 dark:text-gray-100">{ACCOUNT_KIND_LABEL[agreement.receivingAccountKind]}</strong>
                </span>
                <span className="text-gray-600 dark:text-gray-300">
                    Gera boleto <strong className="text-gray-800 dark:text-gray-100">{agreement.payment.instruction === 'BOLETO' ? 'Sim' : 'Não'}</strong>
                </span>
            </div>
        </header>
        <div className="space-y-6 px-5 py-5">
            {agreement.collect ? (
                <>
                    <div className="flex flex-wrap items-center gap-3">
                        <ReadValue label="Política de cobrança">
                            {agreement.policyId
                                ? (
                                    <Link to={collectionPolicyEditPath(agreement.policyId)}
                                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline dark:text-blue-400">
                                        {agreement.policyName || policy?.name || 'Ver política'}
                                        <HiOutlineExternalLink size={14} />
                                    </Link>
                                )
                                : 'Nenhuma política escolhida'}
                        </ReadValue>
                    </div>
                    {policy?.description && <p className="text-sm text-gray-600 dark:text-gray-300">{policy.description}</p>}
                    {policy && <ReadOnlyOrderTable agreement={agreement} policy={policy} />}
                </>
            ) : <p className="text-sm text-gray-500">Esta dívida não é cobrada.</p>}
            <ReadValue label="Exceções / Observações">
                {agreement.notes?.trim() ? <span className="whitespace-pre-wrap">{agreement.notes}</span> : <span className="text-gray-500">Sem observações</span>}
            </ReadValue>
            <AgreementPartiesSummary parties={agreement.parties} />
            <div className="grid grid-cols-1 gap-4 rounded-lg border border-gray-100 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60 sm:grid-cols-2">
                <ReadValue label="Criado em">
                    {formatDateTimeBR(agreement.createdAt)}{agreement.createdBy ? ` · ${agreement.createdBy}` : ''}
                </ReadValue>
                <ReadValue label="Última atualização">
                    {formatDateTimeBR(agreement.updatedAt)}{agreement.updatedBy ? ` · ${agreement.updatedBy}` : ''}
                </ReadValue>
            </div>
        </div>
    </section>
);

export default CollectionAgreementView;
